import { ChevronRightIcon, StopwatchIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import { useGetSettings, useUpdateSettings } from "src/api/General/settings";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import { Loading } from "src/components/ui/loading";
import { useRealmApp } from "src/store/RealmApp";

type Props = {};

export default function TimeTrackingSettings({}: Props) {
  const { instance } = useGetInstance();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings`}>
            <div className="text-gray-800">{instance?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Time Tracking"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="mx-auto mt-20 max-w-3xl px-8">
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md border shadow-sm">
                <StopwatchIcon className="h-4 w-4 text-gray-600" />
                {/* <svg
              width="32px"
              height="32px"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
            >
              <g fill-rule="evenodd" clip-rule="evenodd">
                <path
                  fill="#E01E5A"
                  d="M2.471 11.318a1.474 1.474 0 001.47-1.471v-1.47h-1.47A1.474 1.474 0 001 9.846c.001.811.659 1.469 1.47 1.47zm3.682-2.942a1.474 1.474 0 00-1.47 1.471v3.683c.002.811.66 1.468 1.47 1.47a1.474 1.474 0 001.47-1.47V9.846a1.474 1.474 0 00-1.47-1.47z"
                />
                <path
                  fill="#36C5F0"
                  d="M4.683 2.471c.001.811.659 1.469 1.47 1.47h1.47v-1.47A1.474 1.474 0 006.154 1a1.474 1.474 0 00-1.47 1.47zm2.94 3.682a1.474 1.474 0 00-1.47-1.47H2.47A1.474 1.474 0 001 6.153c.002.812.66 1.469 1.47 1.47h3.684a1.474 1.474 0 001.47-1.47z"
                />
                <path
                  fill="#2EB67D"
                  d="M9.847 7.624a1.474 1.474 0 001.47-1.47V2.47A1.474 1.474 0 009.848 1a1.474 1.474 0 00-1.47 1.47v3.684c.002.81.659 1.468 1.47 1.47zm3.682-2.941a1.474 1.474 0 00-1.47 1.47v1.47h1.47A1.474 1.474 0 0015 6.154a1.474 1.474 0 00-1.47-1.47z"
                />
                <path
                  fill="#ECB22E"
                  d="M8.377 9.847c.002.811.659 1.469 1.47 1.47h3.683A1.474 1.474 0 0015 9.848a1.474 1.474 0 00-1.47-1.47H9.847a1.474 1.474 0 00-1.47 1.47zm2.94 3.682a1.474 1.474 0 00-1.47-1.47h-1.47v1.47c.002.812.659 1.469 1.47 1.47a1.474 1.474 0 001.47-1.47z"
                />
              </g>
            </svg> */}
              </div>
              <div>
                <h2 className="cursor-default text-lg font-medium tracking-wide text-gray-900">
                  Time Tracking
                </h2>
                {/* <p className="text-sm font-light text-gray-500">
              Sync calendar events to enable automations in our AI Note Taker,
              Timesheets and more.
            </p> */}
              </div>
            </div>

            <IntegrationStatus />
          </div>
          <div className="mt-8 pb-20">
            <TimeTracking />
          </div>
        </div>
      </div>
    </div>
  );
}

function IntegrationStatus() {
  const { data: settings } = useGetSettings();
  if (!settings) {
    return <div className="h-7 w-16 animate-pulse rounded-md bg-gray-100" />;
  }
  if (settings.apps?.timeTracking.isEnabled) {
    return (
      <Badge variant={"green"} className="h-7 px-2.5">
        Active
      </Badge>
    );
  }
  return (
    <Badge variant={"gray"} className="h-7 px-2.5">
      Inactive
    </Badge>
  );
}

function TimeTracking() {
  const { data: settings } = useGetSettings();
  // console.log(settings);
  const app = useRealmApp();
  const queryClient = useQueryClient();
  const updateSettingsMutation = useUpdateSettings();
  const navigate = useNavigate();
  return (
    <>
      {!!settings ? (
        <>
          {settings?.apps.timeTracking.isAccessible ? (
            <div>
              <div className="rounded-md border border-gray-200/70 p-8 shadow-sm">
                {!!settings?.apps?.timeTracking.isEnabled ? (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="leading-none text-gray-900">Overview</div>
                      <div className="text-sm font-light leading-relaxed text-gray-600">
                        Our time tracking app helps you track time spent on
                        projects and the stages and tasks within them. It gives
                        you insights into the utilization of your team.
                      </div>
                    </div>
                    <div>
                      <Button
                        variant={"secondary"}
                        onClick={() => {
                          updateSettingsMutation.mutate({
                            update: {
                              apps: {
                                ...settings?.apps,
                                timeTracking: {
                                  ...settings?.apps?.timeTracking,
                                  isEnabled: false,
                                },
                              },
                            },
                          });
                        }}
                      >
                        Disable
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="leading-none text-gray-900">Overview</div>
                      <div className="text-sm font-light leading-relaxed text-gray-600">
                        Our time tracking app helps you track time spent on
                        projects and the stages and tasks within them. It gives
                        you insights into the utilization of your team.
                      </div>
                    </div>
                    <div>
                      <Button
                        onClick={async () => {
                          updateSettingsMutation.mutate({
                            update: {
                              apps: {
                                ...settings?.apps,
                                timeTracking: {
                                  ...settings?.apps?.timeTracking,
                                  isEnabled: true,
                                },
                              },
                            },
                          });
                        }}
                      >
                        Enable
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {settings?.apps?.timeTracking.isEnabled && (
                <>
                  {/* <div className="mt-16 border-b border-gray-200/70 pb-10">
                <div className="space-y-3.5">
                  <div className="font-medium leading-none">Capacity</div>
                  <div className="max-w-prose text-sm font-light text-gray-600">
                    Define the available hours for your team members. This will
                    be used to calculate the capacity & utilization of your
                    team.
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant={"secondary"}
                    className="group relative pr-10"
                    onClick={() => {
                      navigate("/settings/apps/time-tracking/capacity");
                    }}
                  >
                    Go to Capacity Settings
                    <ArrowRightIcon className="absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 transition-all duration-300 group-hover:right-2" />
                  </Button>
                </div>
              </div> */}
                  <div className="mt-16 pb-10">
                    <div className="space-y-3.5">
                      <div className="font-medium leading-none">
                        External Visibility
                      </div>
                      <div className="max-w-prose text-sm font-light leading-relaxed text-gray-600">
                        If this is enabled, external users in external projects
                        will be able to see budgeted hours and tracked hours
                        (billable and non-billable). This is the default setting
                        that is applied to projects at creation. You can change
                        it on a per project level thereafter.
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Checkbox
                        checked={settings?.timeTracking.isExternallyVisible}
                        onCheckedChange={(checked) => {
                          updateSettingsMutation.mutate({
                            update: {
                              timeTracking: {
                                isExternallyVisible: !!checked,
                              },
                            },
                          });
                        }}
                      />
                      <div className="text-sm">
                        Give external users visibility to budgeted and tracked
                        hours
                      </div>
                    </div>
                  </div>
                  {/* <div className="mt-10 pb-10">
                    <div className="space-y-3.5">
                      <div className="font-medium leading-none">Categories</div>
                      <div className="max-w-prose text-sm font-light leading-relaxed text-gray-600">
                        Tracking time in categories helps you analyze your
                        team's time. It enables you to see utilization by
                        category, time spent on projects by category, time spent
                        per team member per category and more.
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button variant={"secondary"}>Go to Categories</Button>
                    </div>
                  </div>
                  <div className="mt-10 pb-10">
                    <div className="space-y-3.5">
                      <div className="font-medium leading-none">Time Off</div>
                      <div className="max-w-prose text-sm font-light leading-relaxed text-gray-600">
                        Define time off periods for your entire team or specific
                        team members. The days that are marked as time off will
                        not be included in the capacity calculations.
                      </div>
                      <div className="max-w-prose text-sm font-light leading-relaxed text-gray-600">
                        Time Off Admins - Jai Thirani
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-2">
                      <Button variant={"secondary"}>View Time Off</Button>
                      <Button variant={"secondary"}>Add Time Off</Button>
                    </div>
                  </div> */}
                  {/* <div className="mt-10 pb-10">
                <div className="space-y-3.5">
                  <div className="font-medium leading-none">
                    Team Exclusions
                  </div>
                  <div className="max-w-prose text-sm font-light text-gray-600">
                    Exclude certain team members from receiving reminders to
                    submit timesheets and being visible in timesheet analytics.
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant={"secondary"}
                    className="group relative pr-10"
                    onClick={() => {
                      navigate("/settings/apps/time-tracking/capacity");
                    }}
                  >
                    Go to Team Exclusions
                    <ArrowRightIcon className="absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 transition-all duration-300 group-hover:right-2" />
                  </Button>
                </div>
              </div> */}
                  {/* <div className="mt-12 border-b border-gray-200/70 pb-12">
                <div className="space-y-4">
                  <div className="font-medium leading-none">
                    Lock Time Entries
                  </div>
                  <div className="max-w-prose text-sm font-light text-gray-600">
                    Prevent team members from editing timesheets based on
                    certain conditions.
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <SecondaryCheckbox />
                      Cannot edit timesheets once approved
                    </div>
                  </div>
                </div>
              </div> */}
                  {/* <div className="mt-6 rounded-lg border border-gray-200/70 p-8 shadow-sm">
                <div className="space-y-4">
                  <div className="font-medium leading-none">
                    Team Exclusions
                  </div>
                  <div className="text-sm font-light text-gray-600">
                    Exclude certain team members from receiving reminders to
                    submit timesheets and being visible in timesheet analytics.
                  </div>
                </div>
                <div className="mt-4">
                  <Button
                    variant={"secondary"}
                    className="group relative pr-10"
                    onClick={() => {
                      navigate("/settings/apps/time-tracking/capacity");
                    }}
                  >
                    Go to Team Exclusions
                    <ArrowRightIcon className="absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 transition-all duration-300 group-hover:right-2" />
                  </Button>
                </div>
              </div>

              <div className="mt-10">
                <div className="flex gap-8">
                  <div className="max-w-[240px] space-y-1">
                    <div className="font-medium">Team Exclusions</div>
                    <div className="text-sm font-light text-gray-600">
                      Exclude certain team members from receiving reminders to
                      submit timesheets and being visible in timesheet
                      analytics.
                    </div>
                  </div>
                  <div>
                    <Button
                      variant={"secondary"}
                      className="group relative pr-10"
                      onClick={() => {
                        navigate(
                          "/settings/apps/time-tracking/team-exclusions"
                        );
                      }}
                    >
                      Go to Exclusions
                      <ArrowRightIcon className="absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 transition-all duration-300 group-hover:right-2" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <div className="space-y-1">
                  <div>Team Exclusions</div>
                  <div>
                    If you want to exclude certain workspace members from
                    getting reminders to track time, seeing them in the
                    analytics,
                  </div>
                </div>
                <div className="space"></div>
              </div> */}
                  {/* <div className="mt-10 rounded-md border p-8 shadow-sm">
                <div className="space-y-4">
                  <div className="leading-none">Lock Time Entries</div>
                  <div className="text-sm font-light leading-relaxed text-gray-600">
                    Prevent team members from editing timesheets after they have
                    been approved.
                  </div>
                </div>
                <div className="space"></div>
              </div>
              <div className="mt-10">
                <div className="space-y-1">
                  <div>Lock Time Entries</div>
                  <div className="text-sm font-light leading-relaxed text-gray-600">
                    Configure timesheets such that team members can't edit them
                    under certain conditions.
                  </div>
                </div>
                <div className="mt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="space-y-2">
                      <div className="text-sm">
                        Lock timesheets once approved
                      </div>
                      <div className="text-sm font-light">
                        This will prevent team members from editing timesheets
                        once they have been approved. They will need to request
                        access to edit.
                      </div>
                    </div>
                    <Switch />
                  </div>
                </div>
                <div className="space"></div>
              </div> */}
                </>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-gray-200/70 p-8 shadow-sm">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="leading-none text-gray-900">Overview</div>
                  <div className="text-sm font-light leading-relaxed text-gray-600">
                    Our time tracking app helps you track time spent on projects
                    and the stages and tasks within them. It gives you insights
                    into the utilization of your team.
                  </div>
                </div>
                <div className="text-sm font-light leading-relaxed text-gray-800 underline">
                  Time Tracking is currently disabled. Please reach out to us if
                  you want to enable it.
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Loading className="mx-auto mt-[10vh]" />
      )}
    </>
  );
}
